import { Box, Popover, Typography } from '@mui/material';
import ColorExt from 'color';
import React, { useCallback, useMemo, useState } from 'react';
import { SketchPicker } from 'react-color';
import Alpha from './Alpha';
import ColorBox from './ColorBox';

type Rgba = {
	r: number | undefined;
	g: number | undefined;
	b: number | undefined;
	a?: number | undefined;
};

type Props = {
	color: string;
	onChange: (hex: string) => void;
	// eslint-disable-next-line react/require-default-props
	disableAlpha?: boolean;
};

const Color: React.FC<Props> = ({
	color,
	onChange,
	disableAlpha,
	...props
}: Props) => {
	const [colorBoxRef, setColorBoxRef] = useState<HTMLDivElement>();
	const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

	const handleChange = useCallback(
		({ rgb: { a = 1, ...rgb } }: { rgb: Rgba }) => {
			const hex = ColorExt(rgb).alpha(a).hexa();
			onChange(hex);
		},
		[onChange]
	);

	// in case we got an invalid color, we are using a fallback
	const validColor = useMemo(() => {
		try {
			ColorExt(color);
			return color;
		} catch (e) {
			return '#FFFFFF';
		}
	}, [color]);

	return (
		<>
			<Box
				{...props}
				onClick={() => {
					setIsColorPickerOpen((state) => !state);
				}}
				ref={setColorBoxRef}
				display="inline-flex"
				border={1}
				alignItems="center"
				width="fit-content"
				justifyContent="space-between"
				borderRadius={1}
				sx={{
					borderColor: 'divider',
					cursor: 'pointer',
					'& > *:last-child': {
						borderRight: 0
					}
				}}
			>
				<Box
					paddingY={1}
					paddingLeft={2}
					display="inline-flex"
					alignItems="center"
					borderRight={1}
					sx={{
						borderColor: 'divider',
						width: 120,
						'& > *:not(:last-child)': { marginRight: 1 }
					}}
				>
					<ColorBox color={validColor} />

					<Typography textTransform="uppercase" variant="body1">
						{ColorExt(validColor).hex()}
					</Typography>
				</Box>
				{!disableAlpha && <Alpha color={validColor} />}
			</Box>
			<Popover
				open={isColorPickerOpen}
				anchorEl={colorBoxRef}
				onClose={() => {
					setIsColorPickerOpen(false);
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
			>
				<Box data-testid="color-picker">
					<SketchPicker
						color={validColor}
						onChange={handleChange}
						disableAlpha={disableAlpha}
					/>
				</Box>
			</Popover>
		</>
	);
};

export default Color;
